import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DoubleHappiness from './DoubleHappiness';
import confetti from '../utils/confetti';

const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const rsvpData = location.state?.rsvpData || {};
  const submissionId = location.state?.submissionId;
  const isAttending = rsvpData.attending === true;

  useEffect(() => {
    // 如果没有 RSVP 数据（说明是直接访问的），重定向到主页
    if (!location.state || !location.state.rsvpData) {
      navigate('/', { replace: true });
      return;
    }
    
    window.scrollTo(0, 0);
    // Trigger confetti animation on mount (only for attending guests)
    if (isAttending) {
      confetti();
    }
  }, [isAttending, location.state, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-red-50 flex items-center justify-center px-4">
      <div className="text-center max-w-2xl">
        <div className="mb-8 animate-bounce">
          <DoubleHappiness size={150} />
        </div>
        
        <h1 className="text-4xl md:text-6xl font-chinese font-bold text-chinese-red mb-6">
          感谢您的回复！
        </h1>
        <p className="text-2xl md:text-3xl font-elegant text-gray-700 mb-4">
          Thank You for Your RSVP!
        </p>
        
        {isAttending ? (
          // 参加婚礼的感谢信息
          <div className="bg-white/90 backdrop-blur rounded-2xl p-8 shadow-xl mb-8 border-2 border-chinese-red/20">
            <div className="text-center mb-6">
              <span className="text-6xl">🎉</span>
              <h2 className="text-2xl font-chinese font-bold text-chinese-red mt-2">
                太棒了！我们期待与您共度美好时光
              </h2>
              <p className="text-xl text-gray-600 mt-2">
                Wonderful! We look forward to celebrating with you
              </p>
            </div>
            
            <div className="space-y-4 text-left">
              <div className="flex items-start">
                <span className="text-2xl mr-3">🗓️</span>
                <div>
                  <p className="text-lg font-semibold text-gray-700">
                    婚礼时间 Wedding Date
                  </p>
                  <p className="text-gray-600">2025年12月27日 | December 27, 2025</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <span className="text-2xl mr-3">📍</span>
                <div>
                  <p className="text-lg font-semibold text-gray-700">
                    婚礼地点 Wedding Venue
                  </p>
                  <p className="text-gray-600">
                    北京朝阳区蟹岛路1号国际会展中心2号馆<br/>
                    Hall 2, Crab Island Convention Center, Chaoyang, Beijing
                  </p>
                </div>
              </div>
              
              {rsvpData.guests > 1 && (
                <div className="flex items-start">
                  <span className="text-2xl mr-3">👥</span>
                  <div>
                    <p className="text-lg font-semibold text-gray-700">
                      出席人数 Number of Guests
                    </p>
                    <p className="text-gray-600">{rsvpData.guests} 位</p>
                  </div>
                </div>
              )}
              
              {rsvpData.dietary_restrictions && (
                <div className="flex items-start">
                  <span className="text-2xl mr-3">🍽️</span>
                  <div>
                    <p className="text-lg font-semibold text-gray-700">
                      饮食需求 Dietary Requirements
                    </p>
                    <p className="text-gray-600">{rsvpData.dietary_restrictions}</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="border-t border-gray-200 pt-6 mt-6">
              <div className="bg-chinese-red/5 rounded-lg p-4">
                <p className="text-md text-gray-700 mb-2 font-semibold">
                  💌 重要提醒 Important Reminders
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 请准时到达，仪式将准时开始</li>
                  <li>• Please arrive on time, ceremony starts promptly</li>
                  <li>• 如有任何变动，请及时联系我们</li>
                  <li>• Contact us immediately if there are any changes</li>
                  <li>• 期待您的到来！Looking forward to seeing you!</li>
                  <li>📧 邮箱 Email: <a href="mailto:yuanzhewu@gmail.com" className="underline hover:text-chinese-pink">yuanzhewu@gmail.com</a><br /></li>
                  <li>📱 电话 Phone: <a href="tel:+8613717823975" className="underline hover:text-chinese-pink">+86 13717823975</a></li>

                </ul>
              </div>
            </div>
          </div>
        ) : (
          // 不能参加的感谢信息
          <div className="bg-white/90 backdrop-blur rounded-2xl p-8 shadow-xl mb-8 border-2 border-gray-300">
            <div className="text-center mb-6">
              <span className="text-6xl">💝</span>
              <h2 className="text-2xl font-chinese font-bold text-gray-700 mt-2">
                感谢您的诚挚回复
              </h2>
              <p className="text-xl text-gray-600 mt-2">
                Thank you for your heartfelt response
              </p>
            </div>
            
            <div className="space-y-4">
              <p className="text-lg text-gray-700 text-center">
                虽然您无法亲临现场，但我们深深感受到了您的祝福与关爱。
              </p>
              <p className="text-lg text-gray-600 text-center">
                Though you cannot be with us in person, we deeply feel your blessings and care.
              </p>
              
              {rsvpData.message && (
                <div className="bg-pink-50 rounded-lg p-4 mt-6">
                  <p className="text-md font-semibold text-gray-700 mb-2">
                    💕 您的祝福 Your Message
                  </p>
                  <p className="text-gray-600 italic">"{rsvpData.message}"</p>
                </div>
              )}
              
              <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-md text-gray-700 mb-2 font-semibold text-center">
                    🌟 我们的心意 Our Heartfelt Thanks
                  </p>
                  <div className="text-sm text-gray-600 space-y-2 text-center">
                    <p>感谢您抽出宝贵时间回复我们的邀请</p>
                    <p>Thank you for taking the time to respond to our invitation</p>
                    <p>您的友谊对我们来说意义重大</p>
                    <p>Your friendship means the world to us</p>
                    <p className="font-semibold text-chinese-red">
                      希望未来能有机会与您相聚！
                    </p>
                    <p className="font-semibold text-gray-700">
                      We hope to celebrate with you in the future!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <Link
            to="/"
            className="inline-block bg-gradient-to-r from-chinese-red to-chinese-pink text-white font-chinese font-bold py-3 px-8 rounded-full shadow-lg transform hover:scale-105 transition-all"
          >
            返回主页 Back to Home
          </Link>
          
          { true && (
            <div className="mt-4">
              <button
                onClick={() => {
                  // WeChat browser detection
                  const isWeChat = /MicroMessenger/i.test(navigator.userAgent);
                  
                  if (isWeChat) {
                    // WeChat browser - use meta tags for sharing (already configured in index.html)
                    // Show a hint to users about how to share
                    alert('请点击微信右上角的"···"按钮，然后选择"分享给朋友"或"分享到朋友圈"\\n\\nPlease tap the "···" button in the top-right corner of WeChat, then select "Share with friends" or "Share to Moments"');
                  } else {
                    // Non-WeChat browsers - use Web Share API or fallback
                    if (navigator.share) {
                      navigator.share({
                        title: '武元哲&张羽彤的婚礼RSVP',
                        text: '请您参加Hans&Yutong的婚礼！2025年12月27日，期待在北京蟹岛国际会展中心见到大家',
                        url: window.location.origin
                      }).catch((error) => {
                        console.log('Share failed:', error);
                        // Fallback to copying URL
                        copyToClipboard();
                      });
                    } else {
                      // Fallback to copying URL
                      copyToClipboard();
                    }
                  }
                  
                  // Helper function to copy URL to clipboard
                  function copyToClipboard() {
                    const url = window.location.origin;
                    if (navigator.clipboard && window.isSecureContext) {
                      navigator.clipboard.writeText(url).then(() => {
                        alert('链接已复制到剪贴板！\\nLink copied to clipboard!');
                      });
                    } else {
                      // Fallback for older browsers
                      const textArea = document.createElement('textarea');
                      textArea.value = url;
                      document.body.appendChild(textArea);
                      textArea.select();
                      try {
                        document.execCommand('copy');
                        alert('链接已复制到剪贴板！\\nLink copied to clipboard!');
                      } catch (err) {
                        alert('无法复制链接，请手动复制: ' + url + '\\nCannot copy link, please copy manually: ' + url);
                      }
                      document.body.removeChild(textArea);
                    }
                  }
                }}
                className="text-chinese-red hover:text-chinese-dark transition-colors"
              >
                <span className="text-2xl mr-2">📱</span>
                分享给朋友 Share with Friends
              </button>
            </div>
          )}
          
          {/* 联系信息 - 不能参加的客人显示联系方式 */}
          {!isAttending && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                如果您的情况有变化，随时可以联系我们：
              </p>
              <p className="text-sm text-gray-600">
                If your situation changes, feel free to contact us anytime
              </p>
              <div className="mt-2 text-chinese-red font-semibold">
                📧 邮箱 Email: <a href="mailto:yuanzhewu@gmail.com" className="underline hover:text-chinese-pink">yuanzhewu@gmail.com</a><br />
                📱 电话 Phone: <a href="tel:+8613717823975" className="underline hover:text-chinese-pink">+86 13717823975</a>
              </div>
            </div>
          )}
        </div>

        {/* Decorative Quote - 根据参加情况显示不同的祝语 */}
        <div className="mt-12">
          {isAttending ? (
            <>
              <p className="text-lg font-chinese text-chinese-dark italic">
                "百年好合，永结同心"
              </p>
              <p className="text-md font-elegant text-gray-600 mt-2">
                "A harmonious union lasting a hundred years"
              </p>
            </>
          ) : (
            <>
              <p className="text-lg font-chinese text-gray-600 italic">
                "千里共婵娟，心意永相连"
              </p>
              <p className="text-md font-elegant text-gray-500 mt-2">
                "Though miles apart, our hearts remain connected"
              </p>
            </>
          )}
          
          {/* 显示提交信息 */}
          {submissionId && (
            <div className="mt-4 text-xs text-gray-400">
              回复编号 Submission ID: #{submissionId}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThankYou;


